import React from 'react';

import CodeIcon from '@mui/icons-material/Code';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailIcon from '@mui/icons-material/Mail';
import { Box, Chip, Collapse, Divider, IconButton, Typography, alpha } from '@mui/material';

import { IntegrationEventAndPayloadModel } from '../../../api/api';
import SchemaSection from '../../EntityDesigner/Components/SchemaSection';

interface Props {
	eventData: IntegrationEventAndPayloadModel;
	expanded: boolean;
	onTagClick: (tag: string) => void;
	onToggle: (eventId: string) => void;
}

const IntegrationEventSection: React.FC<Props> = ({ eventData, expanded, onTagClick, onToggle }) => {
	const eventId = eventData.event.id;
	const toggleExpanded = () => onToggle(eventId);

	return (
		<Box
			sx={{
				borderRadius: 1,
				backgroundColor: 'background.paper',
				border: (theme) => `1px solid ${theme.palette.divider}`,
				px: 2,
				py: 1.5,
				'&:hover': {
					backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
				},
			}}
		>
			<Box
				onClick={toggleExpanded}
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
					<MailIcon color="primary" sx={{ mr: 2, flexShrink: 0 }} />
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="subtitle1" color="text.primary" fontWeight={600} noWrap>
							{eventData.event.name}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{eventData.event.description || 'No description'}
						</Typography>
						<Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
							{eventData.event.tags?.map((tag) => (
								<Chip
									key={tag}
									label={tag}
									size="small"
									variant="outlined"
									onClick={(event) => {
										event.stopPropagation();
										onTagClick(tag);
									}}
									sx={{
										color: 'text.primary',
										borderColor: (theme) => alpha(theme.palette.success.light, 0.55),
										backgroundColor: (theme) => alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.1 : 0.06),
									}}
								/>
							))}
						</Box>
					</Box>
				</Box>
				<IconButton
					size="small"
					onClick={(event) => {
						event.stopPropagation();
						toggleExpanded();
					}}
					sx={{ ml: 1 }}
				>
					{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
				</IconButton>
			</Box>

			<Divider sx={{ my: 2 }} />

			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
					<CodeIcon color="info" sx={{ mr: 2, mt: 0.5 }} />
					<Box sx={{ flex: 1 }}>
						{eventData.payload ? (
							<SchemaSection schema={eventData.payload} />
						) : (
							<Typography variant="body2" color="text.secondary">
								No payload schema defined
							</Typography>
						)}
					</Box>
				</Box>
			</Collapse>
		</Box>
	);
};

export default IntegrationEventSection;
